<!-- eslint-disable vue/require-component-is -->
<template>
  <div class="modal-value-help">
    <TableHeader
      ref="header-ref"
      :attrs="attrs"
      :multiple="multiple"
      :isBusy="isBusy"
      :isEditing="isEditing"
      :totalSelected="selectedValuesLength"
      :search.sync="search"
      :filterButton.sync="filterButton"
      @addNewData="handleAdd"
      @unselectAll="handleUnselectAll"
    />
    <div ref="table-container-ref" class="__table-container">
      <FormulateForm v-model="formValues" @submit="handleSubmit">
        <b-table
          ref="table-ref"
          show-empty
          selectable
          :select-mode="multiple ? 'multi' : 'single'"
          :no-select-on-click="isEditingRow !== null"
          :fields="computedFields"
          :items="items"
          :busy="isBusy"
          class="-w-auto text-nowrap sticky-head-table border"
          @row-clicked="handleRowClicked"
        >
          <!-- Slot: Empty -->
          <template #empty>
            <div v-show="!isBusy" class="text-center my-4">Tidak ada data</div>
          </template>

          <template v-if="attrs.trackBy === 'id' && canSelectAll" #head(checkbox)>
            <div class="form-check">
              <input
                v-model="selectAll"
                class="form-check-input me-1"
                type="checkbox"
                value="all"
                @input="toggleAll"
              />
            </div>
          </template>

          <!-- Slot: Cell Checkbox -->
          <template #cell(checkbox)="{ index, item }">
            <div class="form-check">
              <b-form-checkbox
                v-model="selectedValues[item[attrs.trackBy]]"
                @change="handleCheckboxChange($event, item, index)"
              />
            </div>
          </template>

          <!-- Table Header -->
          <template #head()="data">
            <div
              class="d-flex justify-content-between align-items-center"
              @click="handleSortChange(data)"
            >
              {{ data.label }}
              <template v-if="data.field.help">
                <i
                  :id="`tooltip-data-${data.field.key}`"
                  class="mc-fa"
                  :class="{
                    'icon-outline_info': hoveredKey !== data.field.key,
                    'icon-fill_info': hoveredKey === data.field.key,
                  }"
                  @mouseenter="hoveredKey = data.field.key"
                  @mouseleave="hoveredKey = null"
                />
                <b-popover
                  triggers="hover"
                  placement="topright"
                  :target="`tooltip-data-${data.field.key}`"
                >
                  <div class="popover-text-wrapper">
                    {{ data.field.tooltip }}
                  </div>
                </b-popover>
              </template>
              <template v-if="data.field.isSortable">
                <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                  <i class="mc-icon icon-outline_chevron-up"></i>
                </div>
                <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                  <i class="mc-icon icon-outline_chevron-down"></i>
                </div>
              </template>
            </div>
          </template>

          <!-- Table Data -->
          <template #cell()="{ index, field, value, item }">
            <template v-if="(field.editable || field.isEditable) && isEditingRow == index">
              <FormulateInput
                v-bind="field"
                :rowItem="item"
                :label="null"
                :type="field.type || 'text'"
                :disabled="isSubmitting"
                :help="field?.validation?.includes('required')"
                size="sm"
                validation-behavior="live"
                error-behavior="live"
              />
            </template>

            <template v-else-if="field.isStatus">
              <span :class="field.formatter(get(item, field.key)).class">
                {{ field.formatter(get(item, field.key)).text }}
              </span>
            </template>

            <template v-else>
              <slot v-if="field.isSlot" :name="field.name" :item="item">
                {{ value }}
              </slot>
              <template v-else>
                {{ formatValue(value, field) }}
              </template>
            </template>
          </template>

          <!-- Slot: Action Column (Last Column) -->
          <template #cell(action)="data">
            <div v-if="isEditingRow == data.index">
              <b-button variant="outline" class="__accept-button btn" type="submit">
                <i class="mc-icon icon-fill_checkmark"></i>
              </b-button>

              <b-button
                variant="outline"
                class="__cancel-button btn"
                @click="handleEditCancel(data)"
              >
                <i class="mc-icon icon-outline_close"></i>
              </b-button>
            </div>
          </template>
        </b-table>
      </FormulateForm>

      <!-- Observe visibility to get next page data -->
      <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>

      <!-- Skeleton when getting the data -->
      <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
        <b-skeleton width="2.5%" />
        <b-skeleton width="97.5%" />
      </div>
    </div>

    <!-- TODO: Enhance custom create modal in next feature development -->
    <component :is="attrs.customCreateModal" v-if="attrs['has:createModal']" v-bind="attrs" />
  </div>
</template>

<script>
import TableHeader from './Header'
import isEmpty from 'lodash/isEmpty'
import get from 'lodash/get'

export default {
  name: 'ModalTable',
  components: {
    TableHeader,
  },
  props: {
    isBusy: {
      type: Boolean,
      default: false,
    },

    isEditing: {
      type: Boolean,
      default: false,
    },

    selectedModel: {},

    fullObject: {},

    totalCount: {},

    attrs: {},

    listGetter: {},

    multiple: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:isBusy', 'update:totalCount', 'update:isEditing'],
  data() {
    return {
      items: [],
      ids: [],
      sorts: [],
      page: 1,
      limit: 10,
      search: '',
      filterButton: null,

      selectedValues: {},
      selectedObject: {},

      formValues: {},
      isEditingRow: null,
      isSubmitting: false,
      isPagination: true,
      selectAll: false,
      hoveredKey: null,
    }
  },
  computed: {
    canSelectAll() {
      if (!this.attrs.canSelectAll) return false
      return this.attrs.canSelectAll
    },
    computedFields() {
      if (!this.attrs.tableFields) return []

      const fields = [...this.attrs.tableFields]
      if (this.multiple) {
        fields.unshift({
          key: 'checkbox',
          label: '',
          thStyle: 'width:20px;',
        })
      }

      if (this.isEditingRow !== null) {
        fields.push({
          key: 'action',
          label: '',
          thClass: 'mc-action-col-head',
          tdClass: 'mc-action-cell',
        })
      }

      return fields
    },
    hasNextPage() {
      if (!this.isPagination) return false

      // If totalCount is null or undefined The data has not been fetched yet
      // so we return true to get the actual total count
      if (!this.totalCount) return true
      return this.page * this.limit < this.totalCount
    },
    selectedValuesLength() {
      return Object.keys(this.selectedValues).filter((value) => this.selectedValues[value]).length
    },
  },
  watch: {
    async search(newValue) {
      const opts = {
        limit: this.limit,
        search: newValue != '' ? newValue : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        filterButton: this.filterButton,
      }
      await this.getData(opts, true)
      this.$refs['header-ref'].focusSearch()
    },
    async filterButton(newValue) {
      const opts = {
        limit: this.limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        filterButton: newValue,
      }
      await this.getData(opts, true)
    },
  },
  async mounted() {
    // If there is no selected data from parent
    if (this.multiple && isEmpty(this.selectedModel)) return
    if (this.selectedModel == '' || this.selectedModel == null) return

    const selected = this.multiple ? this.selectedModel : [this.selectedModel]

    // set keys in selectedValues
    selected.forEach((value) => {
      this.$set(this.selectedValues, value, true)
    })

    // if there is no fullObject from parent
    if (isEmpty(this.fullObject) || this.fullObject == undefined) return

    const fullObject = this.multiple ? this.fullObject : [this.fullObject]

    // set fullObject in selectedObject
    fullObject.forEach((obj) => {
      this.$set(this.selectedObject, obj[this.attrs.trackBy], obj)
    })

    await this.$nextTick()

    if (Object.keys(this.selectedValues).length > 0) {
      this.selectAll = true
    }
  },
  beforeDestroy() {
    this.handleHide()
  },
  methods: {
    get,
    toggleAll(event) {
      let checked = event.target.checked
      if (checked) {
        this.handleSelectAll()
      } else {
        this.handleUnselectAll()
      }
    },
    handleAdd() {
      if (this.attrs['has:createModal']) {
        this.$bvModal.show(`${this.attrs.name}-create-data-modal`)
        return
      }

      this.$emit('update:isEditing', true)

      if (this.isEditingRow != null) return
      this.isEditingRow = 0

      this.items = [{ id: 0 }, ...this.items]
    },
    HandleApply() {
      return {
        selectedValues: this.selectedValues,
        selectedObject: this.selectedObject,
      }
    },
    handleHide() {
      this.items = []
      this.ids = []
      this.sorts = []
      this.page = 1
      this.search = ''

      this.isEditingRow = null
      this.isSubmitting = false
    },
    async getData(opts, reset = false) {
      this.$emit('update:isBusy', true)

      if (!this.listGetter) {
        this.$emit('update:isBusy', false)
        return
      }

      const res = await this.listGetter(opts)
      if (!res || res.errors) return

      this.items = reset ? res.data.paginated_result : [...this.items, ...res.data.paginated_result]
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.isPagination = res.isPagination !== undefined ? res.isPagination : true

      this.$emit('update:totalCount', res.metadata.total_count)
      this.$emit('update:isBusy', false)

      await this.$nextTick()

      if (this.isEditing) return
      this.highlightSelectedRow()
    },
    // highlight the row if there is selected value from parent
    highlightSelectedRow() {
      const trackBy = this.attrs.trackBy

      let selected = this.multiple ? this.selectedModel : [this.selectedModel]

      if (!Array.isArray(selected)) return

      selected.forEach((value) => {
        // intentionally used == instead of === because selectedModel can be a string or number (id or uuid)
        const index = this.items.findIndex((item) => item[trackBy] == value)

        // Toggle the row checkbox
        if (index !== -1) {
          this.$refs['table-ref'].selectRow(index)
        }
      })
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return
      // if there is no items and has the search query
      // then we don't have to get data by observer
      if (this.items.length == 0 && this.search) return

      // if total count is null, it means that we haven't fetch the data (initial)
      // do not increment page, but still fetch the data
      this.getData({
        limit: this.limit,
        page: this.totalCount ? ++this.page : this.page,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        filterButton: this.filterButton,
      })

      // Scroll to bottom when pagination is triggered
      // const componentRef = this.$refs['table-container-ref']

      //   this.$nextTick().then(() => {
      //     componentRef.scrollTo?.({
      //       top: componentRef.scrollHeight,
      //       behavior: 'smooth',
      //     })
      //   })
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      // Sorting function is disabled when editing table
      if (this.isEditing) return

      // If this function is called without data params,
      // remove sorting (revert to default)
      if (!data) {
        this.sorts = []
        return
      }

      // If field not sortable or If currently editing a field, disable sort
      if (!data?.field?.isSortable) return

      // Get index of current column sort, if any
      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      // Get the sorting data (key, order)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      // This switch case is used to toggle between these conditions:
      // default - asc - desc - default (repeat)
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData(
        {
          limit: this.limit,
          page: 1,
          search: this.search != '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
          filterButton: this.filterButton,
        },
        true
      )
    },
    // assign selected object to state
    assignSelectedObject(checked, item) {
      if (!this.multiple) {
        this.selectedObject = {}
      }

      if (checked) {
        this.$delete(this.selectedObject, item[this.attrs.trackBy])
        return
      }

      this.$set(this.selectedObject, item[this.attrs.trackBy], item)
    },
    handleRowClicked(item, index) {
      if (this.attrs.hasStoreAction && this.isEditingRow !== null) return

      const trackBy = this.attrs.trackBy

      // Last checked value before the user clicked on the row
      const checked = this.selectedValues[item[trackBy]]

      // To protect when chexbox state (selectedValues)
      // is not synced with b-table select state
      // Force select/unselect to match the checkbox state
      const toggleRowSelection = !checked ? 'unselectRow' : 'selectRow'
      this.$refs['table-ref'][toggleRowSelection](index)

      // assign the fullObject to be emit when applied
      this.assignSelectedObject(checked, item)

      if (this.multiple) {
        this.$set(this.selectedValues, item[trackBy], !checked)
      } else {
        this.selectedValues = {
          [item[trackBy]]: !checked,
        }
      }

      this.$emit('row-selected', this.selectedValuesLength)
    },
    // if the user clicks on the checkbox (not on the row)
    handleCheckboxChange(targetChecked, item, index) {
      this.assignSelectedObject(!targetChecked, item)

      if (targetChecked) {
        this.$refs['table-ref'].selectRow(index)
        return
      }

      this.$refs['table-ref'].unselectRow(index)
    },
    handleUnselectAll() {
      this.selectAll = false
      this.selectedValues = {}
      this.selectedObject = {}
      this.$refs['table-ref'].clearSelected()
    },
    // TODO: Discuss with backend how to handle
    // select all when trackBy is not id
    handleSelectAll() {
      if (this.attrs.trackBy === 'id') {
        // TODO: handle assign the full object in select all
        this.ids.forEach((id) => {
          let item = this.items.find((item) => item.id == id)

          if (item == undefined) {
            item = {
              id: id,
            }
          }

          this.$set(this.selectedValues, id, true)
          this.$set(this.selectedObject, id, item)
        })
      } else {
        throw new Error('Select all is not yet implemented when trackBy is not id')
      }
    },
    transformFields(formValues, fields) {
      const formValuesKeys = Object.keys(formValues)
      const validKeys = formValuesKeys.filter((key) => formValues[key] !== false)

      return Object.assign(
        {},
        ...validKeys.map((key) => {
          const currentField = fields.find((item) => item.key == key)
          const value = formValues[key]

          if (currentField && currentField._type === 'number') {
            const isValid = Boolean(value) || value === '0' || value === 0
            return { [key]: isValid ? Number(value) : null }
          } else if (currentField && currentField.type === 'text') {
            return { [key]: formValues[key].trim().replace(/  +/g, ' ') }
          }

          return { [key]: value }
        })
      )
    },
    async submitStore(data) {
      const createdData = await this.attrs.apiClient.store(data)
      if (createdData.errors) return createdData

      const refetchedData = await this.attrs.apiClient.show(createdData.data.id)
      if (refetchedData.errors) return refetchedData

      this.ids.push(refetchedData.data.id)
      this.$emit('update:totalCount', this.totalCount + 1)

      this.$mcToast.success('Data berhasil ditambahkan')

      return {
        ...data,
        ...refetchedData.data,
      }
    },
    async handleSubmit(data) {
      if (this.isSubmitting) {
        this.$mcToast.warning('Mohon tunggu')
        return
      }

      this.isSubmitting = true

      const transformedData = this.transformFields(data, this.computedFields)

      let newData = null
      newData = await this.submitStore(transformedData)

      if (newData.errors) {
        this.isSubmitting = false
        return
      }

      // Replace data on the table
      if (newData !== null) {
        this.items.splice(this.isEditingRow, 1, newData)
      }

      this.isEditingRow = null
      this.isSubmitting = false
      await this.$nextTick()

      this.handleRowClicked(newData, 0)

      // to highlight the new row
      this.$refs['table-ref'].selectRow(0)
      this.$emit('update:isEditing', false)
    },
    async handleEditCancel() {
      if (this.isSubmitting) return

      const [, ...newItems] = this.items
      this.items = newItems
      this.isEditingRow = null
      await this.$nextTick()

      this.highlightSelectedRow()
      this.$emit('update:isEditing', false)
    },
    formatValue(value, field) {
      if (field.formatValue === undefined) {
        return value
      }

      return field.formatValue(value)
    },
  },
}
</script>

<style lang="scss" scoped>
.__table-container {
  border: 1px solid #ebecef;
  height: 300px;
  overflow-y: auto;

  :deep(.table) {
    --bs-table-active-bg: #e9f2fc;

    thead {
      tr {
        th {
          position: relative;
          &:has(.sort-icon-up),
          &:has(.sort-icon-down) {
            padding-right: 30px;
          }

          .sort-icon-up {
            position: absolute;
            right: 10px;
            top: 8px;
            opacity: 0.7;
          }
          .sort-icon-down {
            position: absolute;
            right: 10px;
            bottom: 8px;
            opacity: 0.7;
          }

          .mc-fa {
            font-size: 16px;
          }
        }
      }
    }

    // Action column
    .mc-action-col-head {
      width: 80px;
    }

    // Action cell
    .mc-action-cell {
      vertical-align: middle;

      & > div {
        display: flex;
        padding: 0 4px;
        justify-content: space-between;
      }

      .mc-icon:before {
        font-size: 20px;
      }

      .btn {
        padding: 0;
      }

      .btn:hover {
        border-color: transparent;
      }

      .__cancel-button .mc-icon:before {
        color: $danger;
      }

      .__disabled .mc-icon:before {
        opacity: $btn-disabled-opacity;
        cursor: not-allowed;
      }

      .__accept-button .mc-icon:before {
        color: $success;
      }
    }
  }
}

.popover-text-wrapper {
  font-family: $font-family-sans-serif;
  font-size: 12px;
  background-color: $mep-color-text-400 !important;
  color: $white !important;
  max-width: 200px;
  min-width: fit-content;
  padding: 6px 12px;
}
</style>
